import type { SeriesReservationAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import SATSeriesQ from './SATSeriesQ'
import SeriesQ from './SeriesQ'
import UserQ from './UserQ'

const SeriesReservationQ = {
  ...makeQueryModelV2<SeriesReservationAttributes>('series_reservation')([
    'id',
    'user_id',
    'user_email',
    'reserver_user_id',
    'series_id',
    'created_at',
    'updated_at',
    'deleted_at',
    'expired_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Series: {
      query: SeriesQ,
      foreignKey: SeriesQ.id,
      sourceKey: SeriesReservationQ.series_id,
    },
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: SeriesReservationQ.user_id,
    },
  }),

  isSATSeries: (): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SATSeriesQ.where({ seriesId: SeriesReservationQ.series_id })})`,

  isActive: (): SQLFragment<boolean> => SQL`${SeriesReservationQ.expired_at} > NOW()`,

  // TODO: Remove the joins from this query, it doesn't need to be this expensive. The consumers can fetch the relevant data themselves.
  getActiveSATBootcampSelfReservations: ({
    userId,
    limit = null,
  }: {
    userId: number | SQLFragment<number>
    limit?: number | null
  }): SQLFragment<SeriesReservationAttributes> =>
    SeriesReservationQ.where(
      {
        userId,
        reserverUserId: userId,
        limit,
        include: [
          {
            association: 'Series',
            required: true,
            include: [
              {
                association: 'User',
                required: true,
              },
              {
                association: 'SATSeries',
                required: true,
                include: [
                  {
                    association: 'SATTest',
                    required: true,
                  },
                ],
              },
              {
                association: 'Course',
                required: true,
              },
            ],
          },
        ],
        order: [[SeriesReservationQ.expired_at, 'asc']],
      },
      SeriesReservationQ.isActive()
    ),

  where: (
    {
      id,
      paranoid = true,
      seriesId,
      userId,
      userEmail,
      reserverUserId,
      include = [],
      limit = null,
      groupBy = null,
      order = null,
    }: {
      id?: number | null | SQLFragment<number>
      paranoid?: boolean
      seriesId?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      userEmail?: string | null | SQLFragment<number>
      reserverUserId?: number | null | SQLFragment<number>
      include?: IncludeAssociations
      limit?: number | null
      groupBy?: GroupBy | null
      order?: QueryOrder | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SeriesReservationAttributes> => SQL`
            FROM ${SeriesReservationQ}
            ${includeAssociationsToSql(SeriesReservationQ, include)}
            WHERE
                ${id != null ? SQL`${SeriesReservationQ.id} = ${id} AND` : SQL``}
                ${seriesId != null ? SQL`${SeriesReservationQ.series_id} = ${seriesId} AND` : SQL``}
                ${userId != null ? SQL`${SeriesReservationQ.user_id} = ${userId} AND` : SQL``}
                ${
                  userEmail != null
                    ? SQL`${SeriesReservationQ.user_email} = ${userEmail} AND`
                    : SQL``
                }
                ${
                  reserverUserId != null
                    ? SQL`${SeriesReservationQ.reserver_user_id} = ${reserverUserId} AND`
                    : SQL``
                }
                ${paranoid ? SQL`${SeriesReservationQ.deleted_at} IS NULL AND` : SQL``}
                ${customWhere != null ? customWhere : SQL`TRUE`}
            ${queryGroupByToSQL(groupBy)}
            ${queryOrderToSQL(order)}
            ${queryLimitToSQL(limit)}
        `,
}

export default SeriesReservationQ
